import _ from 'lodash'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import {AdminDivision, Country} from '@/modules/geo/types'
import Rate from '@/modules/shipping/model/Rate'
import Weight from '@/modules/shipping/model/Weight'
import RegionTypes from '@/modules/shipping/model/RegionTypes'

/**
 * Region de envío
 *
 * Representa un país, división administrativa sub-estatal o zona de alguna
 * de las anteriores
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export default class Region {
    public id = 0
    /**
     * Zona
     *
     * Indica si la región es una zona de países o divisiones administrativas. En el
     * caso afirmativo las regiones de la zona se almacenarán en la propiedad
     * "children"
     */
    public isZone = false

    /**
     * Regiones hijas
     *
     * En el caso de que la región sea una zona esta propiedad almacenará las regiones
     * que pertenecen a la zona
     */
    public children: Region[] = []

    /**
     * ID de la entidad geográfica
     *
     * Almacena el identificador de la entidad geográfica relacionada con la entidad.
     * Para mas información ver la propiedad _geodata.
     *
     * Esta propiedad es enviada y almacena en el servidor
     */
    public regionId = 0
    public isExcluded = false
    public weights: Weight[] = []
    public rate: Rate = new Rate()
    public freeShippingFrom = 0
    private _name = ''
    private _type: RegionTypes

    public get type () {
        return this._type
    }

    /**
     * Cambia el tipo de región
     *
     * @param value
     */
    public set type (value: RegionTypes) {
        const old = this._type
        // Los valores son idénticos
        if (value === old) {
            this._type = value

            return
        }

        // La región es una zona, al cambiar de tipo se restablece la lista de regiones
        // hijas
        if (this.isZone) {
            this.children = []
            old === RegionTypes.AdminDivision && (this.geodata = null)
        }

        this._type = value
    }

    /**
     * Almacena una referencia a la entidad geográfica relacionada con la región.
     *
     * - Si la región es un país la propiedad almacenará una instancia de la
     *   entidad Country con la información del pais.
     *
     * - Si la región es una división administrativa la propiedad almacenará
     *   una instancia de AdminDivision
     *
     * - Si la región es una zona de países la propiedad permanecerá vacía.
     *
     * - Si la región es una zona de divisiones administrativas la propiedad
     *   almacenará una instancia de Country con el pais al que pertenecen
     *   las divisiones.
     */
    private _geodata: Country | AdminDivision | null = null;

    /**
     * Identificador temporal utilizado por la aplicación
     */
    // public tempId = _.uniqueId('region_')
    public tempId = ''

    /**
     * Indica si la región es un país
     */
    get isCountry (): boolean {
        return this.type === RegionTypes.Country
    }

    /**
     * Indica si la región es una zona de divisiones administrativas
     *
     * @return { boolean }
     */
    get isAdminDivisionZone () {
        return this.type === RegionTypes.AdminDivision && this.isZone
    }

    /**
     * Indica si la región es una zona de países
     */
    get isCountriesZone () {
        return this.type === RegionTypes.Country && this.isZone
    }

    /**
     * Permite obtener el nombre de la región. Si ningún nombre ha sido
     * declarado se intentará calcular uno en base al tipo y entidad
     * geográfica.
     */
    get name (): string {
        if (!this.isZone && this.geodata) {
            return this.geodata.name
        }

        if (this.isAdminDivisionZone && !this._name) {
            const children = this.children
            if (!children.length) {
                return ''
            }

            switch (true) {
                case !children.length: return this._name || ''
                case children.length === 1: return children[0].name
                case children.length === 2: return `${children[0].name} y ${children[1].name}`
                case children.length > 2:
                    const copy = [...children]
                    const last = copy.pop()
                    return copy.map(c => c.name).join(', ') + ` y ${last!.name}`
                default:
                    return ''
            }
        }

        return this._name
    }

    set name (name: string) {
        this._name = name
    }

    /**
     * Elimina un peso de la lista pesos declarados en la regiónç
     *
     * @param weight
     */
    public removeWeight (weight: number) {
        const index = this.weights.findIndex(w => w.weight === weight)
        if (index === -1) {
            return this
        }

        this.weights.splice(index, 1)

        return this
    }

    /**
     * Retorna el precio de un determinado peso registrado en la región
     *
     * @param weight
     */
    public getPrice (weight: number): number {
        const exists = this.weights.find(w => w.weight === weight)
        if (!exists) {
            return 0
        }

        return exists.price
    }

    /**
     * Retorna la entidad con la información geográfica de la región
     */
    get geodata (): Country | AdminDivision | null {
        return this._geodata
    }

    set geodata (geodata: Country | AdminDivision | null) {
        if (!geodata) {
            this.regionId = 0
            this._geodata = null

            return
        }

        this.regionId = geodata.id
        this._geodata = geodata
    }

    public static convert (data: KeyValuePair): Region {
        return this.convertRegion(data)
    }

    private static convertRegion (data: KeyValuePair, parent: Region | null = null) {
        const region = new Region()
        region.id = data.id
        region.type = data.type
        region.name = data.name || ''
        region.regionId = data.region_id || 0
        region.isZone = data.is_zone
        region.rate = new Rate()
        region.isExcluded = data.is_excluded
        region.weights = data.weights.map((weight: KeyValuePair) => Weight.convert(weight))
        region.freeShippingFrom = data.free_shipping_from

        if (region.type === RegionTypes.Country || region.isAdminDivisionZone) {
            region.geodata = Country.convert(data.geodata)
        }

        if (region.type === RegionTypes.AdminDivision && !region.isZone) {
            region.geodata = AdminDivision.convert(data.geodata)
        }

        const children = data.children
        if (Array.isArray(children) && children.length > 0) {
            region.children = this.mapRegions(children, region)
        }

        return region
    }

    private static mapRegions (regions: KeyValuePair[], parent: Region | null = null) {
        return regions.map(region => this.convertRegion(region, parent))
    }

    /**
     * Crea una nueva región basada en un país
     *
     * @param country
     */
    public static ofCountry (country: Country): Region {
        const region = new this()
        region.geodata = country
        region.type = RegionTypes.Country
        region.name = country.name

        return region
    }

    /**
     * Crea una región en base a la división administrativa
     *
     * @param division
     */
    public static ofAdminDivision (division: AdminDivision) {
        const region = new this()
        region.geodata = division
        region.type = RegionTypes.AdminDivision
        region.name = division.name

        return region
    }

    /**
     * Convierte la entidad a un objeto serializable por JSON
     */
    public serialize (): KeyValuePair {
        const data: KeyValuePair = {
            name: this.name,
            region_id: this.regionId,
            type: this.type,
            is_excluded: this.isExcluded,
            zone: this.isZone,
            free_shipping_from: this.freeShippingFrom,
            rate: this.rate,
            weights: (this.weights || []).map(w => w.serialize()),
            children: (this.children || []).map(r => r.serialize()),
            geodata: this.geodata,

        }

        if (this.id) {
            data.id = this.id
        }

        return data
    }
}
